import type {FetchResult} from '@apollo/client';
import {ApolloClient, ApolloLink, InMemoryCache, HttpLink, Observable, from} from '@apollo/client';
import {setContext} from '@apollo/client/link/context';
import {RetryLink} from '@apollo/client/link/retry';
import {getMainDefinition} from '@apollo/client/utilities';
import {fetchAuthSession} from 'aws-amplify/auth';
import {persistCache} from 'apollo3-cache-persist';
import localforage from 'localforage';
import {getDefaultStore} from 'jotai';
import {withTimeout} from './withTimeout';
import {getOnlineState} from './onlineState';

const httpLink = new HttpLink({
   uri: import.meta.env.VITE_GRAPHQL_ENDPOINT ?? '/graphql',
});

// Added 2026-08-28 (Duane): a request whose connection drops mid-flight —
// weak signal, a captive network with no real route out, similar — can
// leave the browser's own fetch() neither resolving nor rejecting for a
// very long time (well past what a user will wait for). Nothing upstream
// of HttpLink times this out on its own: Apollo has no built-in request
// timeout, and per this same offline QA pass BOTH paths that depend on a
// request eventually settling one way or another were hit by it —
// useOfflineMutation.ts's submit() online-first attempt (DailyReportPage's
// Submit button stayed disabled/"submitting" indefinitely instead of
// falling back to the offline queue) and every watchQuery's own network
// leg under the app's default cache-and-network fetchPolicy
// (WellsListPage's progress bar spun indefinitely even though cached data
// was already showing on screen — `loading` doesn't resolve to false
// until the network leg settles, however long that takes).
//
// This link gives EVERY operation (query or mutation) that reaches
// httpLink a hard ceiling: an AbortController wired into the request's
// fetchOptions.signal (HttpLink's own documented hook for this), aborted
// after a timeout if the request hasn't settled by then. An aborted
// fetch rejects with a DOMException (name 'AbortError'), which flows to
// call sites as an ordinary network-style error —
// useOfflineMutation.ts's isNetworkError check was widened (now also
// matching 'abort'/'timeout', not just 'network'/'fetch') so a timed-out
// mutation still queues for later retry instead of surfacing as a hard
// failure. useOfflineSync.ts's own per-mutation withTimeout race (added
// the same week, for the replay-on-reconnect path specifically) is left
// in place as defense in depth — harmless overlap, not a conflict, since
// both settle on essentially the same bound.
//
// Added 2026-08-28 (Duane): "the 15 second wait seems very long when the
// mobile device is offline and known to be offline." The 15s ceiling
// exists for the hard case this link was built for — a connection that
// LOOKS up but isn't actually routing anywhere, where a real request is
// genuinely still worth waiting out. But when we already know there's no
// real reachability, a request has no radio to even attempt a handshake
// over and is essentially guaranteed to fail immediately. Mutations skip
// this link entirely in that case (useOfflineMutation.ts's submit()
// checks online state before ever calling client.mutate()), but a
// watchQuery's network leg fires unconditionally regardless of app-level
// online state — that's the case this shorter ceiling is for, so a query
// doesn't sit "loading" for a full 15s against a connection that was
// never going anywhere.
//
// UPDATED 2026-08-31 (Duane): "offline detection is not working
// correctly" — this used to read `navigator.onLine` fresh at request
// time instead of isOnlineAtom purely to dodge a circular import
// (authAtoms.ts imports this file for fetchMyUserProfile's
// apolloClient.query call). But navigator.onLine is exactly the signal
// that turned out not to be trustworthy on its own — see
// lib/onlineState.ts's top comment — so this now reads that module's
// actively-probed value instead. lib/onlineState.ts has zero app
// imports, so pulling it in here creates no circular dependency, and it
// is kept in sync with isOnlineAtom on every write (see authAtoms.ts) —
// this is the same ground truth the rest of the app's UI reacts to, not
// a second, possibly-disagreeing signal.
// UPDATED 2026-09-12 (Duane: "change all the timeouts to 25s", part of
// fixing the OGP03I Export false-"offline" report — see authFailureLink's
// own 2026-09-12 update below for the actual bug): raised from 10000.
// RunTicket_AttachPhoto's own override (added 2026-09-10 for a mutation
// that genuinely took ~10s) no longer needs to be separate — 15s was
// already the top of the "15 or 25 seconds" range Duane approved for it
// that day, so one uniform online ceiling now covers both cases.
const ONLINE_REQUEST_TIMEOUT_MS = 25000;
// Deliberately NOT raised to match — see the 2026-08-28 comment above:
// this short ceiling exists specifically for when the app already
// believes it's offline, so a request with no real chance of connecting
// fails fast instead of making the user wait. Raising it to 15s would
// undo that fix.
const OFFLINE_REQUEST_TIMEOUT_MS = 1000;

// Per-operation override for a request that's a fundamentally different
// shape than the rest of the app's mutations/queries and genuinely needs
// more room than the app-wide ONLINE_REQUEST_TIMEOUT_MS ceiling. Empty
// for a while — 2026-09-12's uniform 15s bump absorbed the one operation
// (RunTicket_AttachPhoto) that previously needed its own entry here; add
// one back only with a real captured trace, same evidence bar as that
// history.
//
// RunTicket_AllWhere ADDED 2026-09-14 (Duane, real captured trace: the
// OGP03I Export's own run of this query — unrestricted, every run
// ticket ever created, no server-side date filter available at all, see
// docs/run-ticket-pickup-date-range-spec.md — took 12.15s end-to-end per
// the browser's Network tab, close enough to the 15s ceiling to abort
// intermittently). 30s gives real headroom above that observed number
// without masking a genuine hang — a request that's actually stuck still
// times out, just later. Revisit (lower this back down, or remove the
// entry) once that spec lands and the query can be scoped server-side
// instead of over-fetching and racing the clock.
// Report_TankAllocations ADDED 2026-09-14 (Duane: "Report_TankAllocations
// aborted after 15107ms" — reported the SAME page load as the
// DailyWellReport_AllWhere timeout above/below). In isolation this
// query only took ~3.2s live against aw.fhgtesting.net — nowhere near
// 15s — so the real fix was giving Ogp03iExportPage.tsx its own
// trimmed DailyWellReport_AllWhere query (see
// Ogp03iDailyWellReport_AllWhere.graphql's header comment: that one
// alone was taking 12.5s on this page, most likely dragging every
// other concurrent query on the same page load — including this one —
// past the shared ceiling with it). This override is a safety net on
// top of that real fix, not a substitute for it: Report_TankAllocations
// is also used unscoped (every well) by TanksAllocationsReportPage, and
// "as of one date" queries like this one only ever get slower as more
// history accumulates behind that date, so some headroom here is cheap
// insurance. Revisit if it's still tripping after the real fix has had
// time to prove itself.
const OPERATION_TIMEOUT_OVERRIDES_MS: Record<string, number> = {
   RunTicket_AllWhere: 30000,
   Report_TankAllocations: 30000,
};

const timeoutLink = new ApolloLink((operation, forward) => {
   const controller = new AbortController();
   const wasOnline = getOnlineState();
   const timeoutMs =
      OPERATION_TIMEOUT_OVERRIDES_MS[operation.operationName ?? ''] ??
      (wasOnline ? ONLINE_REQUEST_TIMEOUT_MS : OFFLINE_REQUEST_TIMEOUT_MS);
   // ADDED 2026-09-10 (Duane's report: a run ticket save for a ticket
   // with a scanned photo showed up as "(canceled)" in the Network tab,
   // and the edit drawer closed as if the save had just been queued for
   // later - even though several other requests right around it
   // succeeded normally). This link's own abort (below) and a genuine
   // dropped connection are indistinguishable after the fact from the
   // Network tab alone - both just show "(canceled)". Logging which
   // ceiling applied and how long the request actually ran before this
   // fired turns "why did this cancel" from a guess into an answer the
   // next time it happens: hitting the full ONLINE_REQUEST_TIMEOUT_MS
   // means the server (or this specific operation) was genuinely slow to
   // respond, not that the connection dropped.
   const startedAt = Date.now();
   const timer = setTimeout(() => {
      console.warn(
         `${operation.operationName ?? 'GraphQL operation'} aborted after ${Date.now() - startedAt}ms - hit the ` +
            `${wasOnline ? 'online' : 'offline'} timeout ceiling (${timeoutMs}ms)`,
      );
      controller.abort();
   }, timeoutMs);
   const existingFetchOptions = (operation.getContext().fetchOptions ?? {}) as Record<string, unknown>;
   operation.setContext({
      fetchOptions: {...existingFetchOptions, signal: controller.signal},
   });
   return new Observable<FetchResult>((observer) => {
      const subscription = forward(operation).subscribe({
         next: (result) => observer.next(result),
         error: (err) => {
            clearTimeout(timer);
            observer.error(err);
         },
         complete: () => {
            clearTimeout(timer);
            observer.complete();
         },
      });
      return () => {
         clearTimeout(timer);
         subscription.unsubscribe();
      };
   });
});

const authLink = setContext(async (_request, {headers}) => {
   try {
      const session = await fetchAuthSession();

      return {
         headers: {
            ...headers,
            accesstoken: session?.tokens?.accessToken?.toString() || '',
         },
      };
   } catch (err) {
      // No session available (e.g. offline-cached mode, or a genuinely
      // expired/revoked session — fetchAuthSession() throws for both,
      // with no reliable way to tell them apart from the error alone;
      // see bootstrapAuthAtom's own comment on this same ambiguity) —
      // proceed without an accesstoken header; queries relying on the
      // cache (fetchPolicy: 'cache-only'/'cache-first') will still
      // resolve from Apollo's cache for offline use, and everything
      // else will 401 (see authFailureLink below, which tracks
      // whether that keeps happening and signs the user all the way
      // out if so).
      //
      // Logged rather than silently swallowed — this exact catch is
      // the reason a request can go out with NO accesstoken header at
      // all (as opposed to the success path above, which always sets
      // one, even if it ends up empty). Without this log there was no
      // way to tell, after the fact, whether a given 401 happened
      // because fetchAuthSession() itself failed (and why) versus some
      // other cause.
      console.warn('fetchAuthSession() failed — sending this request with no accesstoken header:', err);
      return {headers};
   }
});

/**
 * Every GraphQL request in this app rides on the Cognito access token
 * authLink attaches above. A 401 back from the API means the server
 * rejected that token outright (expired, revoked, or never attached at
 * all — see authLink's catch above). Per Duane (2026-08-25): a single
 * 401, or any stretch where the network or the backend itself is down,
 * must NOT force a logout — only a run of 401s that keeps happening and
 * isn't resolving itself should. So this tracks a STREAK of consecutive
 * unauthorized responses across every request the app makes, reset by
 * any request that comes back NOT unauthorized, and only forces a
 * logout once that streak reaches UNAUTHORIZED_STREAK_THRESHOLD in a
 * row with nothing successful in between:
 *
 *   - A single 401 (a token that hadn't refreshed yet, a one-off
 *     backend hiccup) — next request succeeds, streak resets to 0,
 *     never reaches the threshold. No logout.
 *   - The network drops, or the backend is down/returns 5xxs — these
 *     never have statusCode 401 at all (see the `error` branch below),
 *     so they never touch the streak either way. No logout, no matter
 *     how long the outage lasts — exactly Duane's "don't log out for a
 *     hiccup, or if the network/server is down" case.
 *   - The session is actually expired/revoked — EVERY subsequent
 *     request 401s, nothing resets the streak, it reaches the
 *     threshold, and the user is signed out — Duane's "getting all 401
 *     errors and it isn't resolving itself" case.
 *
 * Implemented as a plain ApolloLink (rather than `onError`, which has
 * no hook for a *successful* response) so it can observe both outcomes
 * needed to track a streak: `next` sees graphQL-level responses
 * (including an UNAUTHENTICATED error riding along on an otherwise-200
 * response, which some resolvers send instead of a real HTTP 401), and
 * `error` sees network-level failures, of which only ones carrying
 * `statusCode === 401` count. It never consumes or alters the
 * response/error itself — every result and error is passed on to
 * `observer` exactly as received, so nothing downstream (a call site's
 * own errorPolicy: 'all' + data-presence check, for instance) changes
 * behavior.
 */
const UNAUTHORIZED_STREAK_THRESHOLD = 3;

let consecutiveUnauthorizedCount = 0;

// Sign-out is itself async (amplifySignOut() + the dynamic import
// below), and a streak reaching the threshold can be driven by several
// requests landing close together — this stops each of them from
// independently kicking off its own signOutAtom dispatch once the
// threshold's already been hit for this streak. Reset in `.finally()`
// (not left true forever) so a *future* session, after signing back
// in, can trigger this again if it also runs into a bad streak.
let forcingLogout = false;

function hasUnauthenticatedGraphQLError(result: FetchResult): boolean {
   return (result.errors ?? []).some((e) => e.extensions?.code === 'UNAUTHENTICATED');
}

// ADDED 2026-08-31 (Duane): "can't you detect network issues from the
// graphQL network errors returned? If you didn't detect offline before
// the API call you will know after." Every real request this app makes
// already flows through this link, so it doubles as a live reachability
// signal — no need to wait for useAuthBootstrap.ts's next
// PROBE_INTERVAL_MS poll tick when a request outcome already just told
// us the true answer. A response that carries an HTTP status at all
// (including an error one — a 500, a malformed body) proves the network
// path is genuinely live, whatever else went wrong with that particular
// request; a status-less network error (a raw fetch/TypeError) means the
// request never reached anything and is treated as offline.
//
// UPDATED 2026-09-12 (Duane: "OGP03I Export is showing offline but I am
// not offline... the last graphql API call is (canceled)"): a
// status-less error used to also include timeoutLink's own AbortError
// once its ceiling is hit — but that only proves ONE request ran long
// (this page's month-wide, all-wells DailyWellReport_AllWhere pull is a
// good candidate), not that the network is down. Nothing else in this
// link chain can produce an AbortError here — HttpLink only creates its
// own AbortController when the operation doesn't already carry a signal
// (see createHttpLink.js), and timeoutLink always sets one — so an
// AbortError reaching this link is always self-inflicted, never a real
// dropped connection. It's now excluded from this reachability signal
// entirely (see the `error` handler below): it neither marks the app
// online nor offline, leaving isOnlineAtom exactly as it was.
//
// Goes through isOnlineAtom (dynamically imported — see recordUnauthorized
// above for why this file can't statically import authAtoms.ts) rather
// than writing lib/onlineState.ts directly, so React-rendered UI (the
// Offline chip, the sync icon, Submit's reportsBlockingSubmit gate) all
// see the update in the same render pass as apolloClient.ts's own
// timeoutLink does — isOnlineAtom's setter keeps both in sync on every
// write (see authAtoms.ts's own comment on isOnlineAtom).
function markOnlineFromRequestOutcome(reachable: boolean) {
   if (getOnlineState() === reachable) return; // already believed — skip the store write
   void import('@/state/authAtoms').then(({isOnlineAtom}) => getDefaultStore().set(isOnlineAtom, reachable));
}

function recordUnauthorized() {
   consecutiveUnauthorizedCount += 1;
   if (consecutiveUnauthorizedCount < UNAUTHORIZED_STREAK_THRESHOLD || forcingLogout) return;

   forcingLogout = true;
   // Dynamically imported, not statically: authAtoms.ts itself imports
   // apolloClient (for fetchMyUserProfile's apolloClient.query call), so
   // a static `import {signOutAtom} from '@/state/authAtoms'` at the top
   // of this file would make the two modules import each other. By the
   // time a real unauthorized streak can happen, authAtoms.ts is already
   // loaded (App.tsx pulls it in well before any query fires), so this
   // just resolves the already-cached module.
   //
   // getDefaultStore() rather than a React hook: this file isn't a
   // component, and the app never wraps itself in its own jotai
   // `<Provider store={...}>` (see App.tsx) — every useAtomValue/
   // useSetAtom in the app already reads/writes this same implicit
   // default store, so this reaches that one real store, not a second
   // one. signOutAtom is the exact same sign-out path TopNav/BottomNav's
   // own "Sign Out" button already uses — a real amplifySignOut() call,
   // not just a local status flip. RequireAuth.tsx then redirects to the
   // login page on its own once authStatusAtom flips to
   // 'unauthenticated', same as every other unauthenticated case — no
   // navigation logic needed here.
   void import('@/state/authAtoms')
      .then(({signOutAtom}) => getDefaultStore().set(signOutAtom))
      .finally(() => {
         forcingLogout = false;
         consecutiveUnauthorizedCount = 0;
      });
}

const authFailureLink = new ApolloLink((operation, forward) => {
   return new Observable<FetchResult>((observer) => {
      const subscription = forward(operation).subscribe({
         next: (result) => {
            if (hasUnauthenticatedGraphQLError(result)) {
               recordUnauthorized();
            } else {
               consecutiveUnauthorizedCount = 0;
            }
            // A response came back at all — the network path is live.
            markOnlineFromRequestOutcome(true);
            observer.next(result);
         },
         error: (networkError) => {
            const statusCode =
               networkError && typeof networkError === 'object' && 'statusCode' in networkError
                  ? (networkError as {statusCode?: unknown}).statusCode
                  : undefined;
            if (statusCode === 401) {
               recordUnauthorized();
            }
            // Any other network error — offline, DNS failure, timeout, a
            // 5xx because the backend itself is down — is left alone
            // entirely: it neither counts toward nor resets the streak.
            // Per Duane, the network or server being down must never by
            // itself contribute to a forced logout.
            //
            // Separately (see markOnlineFromRequestOutcome's own
            // comment above): a status code present at all — even a 401
            // or a 500 — still proves reachability. A status-less error
            // means genuinely offline UNLESS it's timeoutLink's own
            // AbortError (this app hitting its own timeout ceiling, never
            // a real dropped connection — see the block comment above) —
            // that case is inconclusive about reachability, so it's
            // skipped entirely here rather than forced to false.
            const isSelfInflictedTimeout =
               !!networkError &&
               typeof networkError === 'object' &&
               (networkError as {name?: unknown}).name === 'AbortError';
            if (!isSelfInflictedTimeout) {
               markOnlineFromRequestOutcome(statusCode !== undefined);
            }
            observer.error(networkError);
         },
         complete: () => observer.complete(),
      });
      return () => subscription.unsubscribe();
   });
});

// Kept as a standalone reference (rather than pulling it back off
// apolloClient.cache) so enableOfflineCachePersistence below can
// pass the exact same instance to persistCache.
//
// Lease.wells gets a merge policy because it's written into the
// normalized cache from more than one query shape at once: every
// generated operation that touches a Well also selects a nested
// `lease { ... wells { id } }` (generate-operations.ts selects every
// field recursively, including relationship back-references, whether
// anything reads them or not — see toAdminWell in adminMappers.ts,
// which only ever reads well.lease.name/id, never well.lease.wells).
// That nested selection normalizes into the exact same cache slot as
// Lease_ById's own direct `wells` field, since Apollo keys cache
// fields by (typename, id, fieldName, args) with no notion of which
// operation the write came from. With no merge function, Apollo's
// default behavior is to replace the whole array with whatever wrote
// it last — and LeaseEditPage's addWell/removeWell trigger exactly
// this race: well_CreateUpdate's own response, then a Well_All
// refetch (nested, unused lease.wells) and a Lease_ById refetch
// (direct, actually-used wells) all land within moments of each
// other, in no guaranteed order. Well_All is the heavier query (every
// well in the system, each with its own nested lease), so it reliably
// resolves after Lease_ById and reliably clobbers the correct result
// — this is what made "adding a well to a lease" look completely
// broken rather than just occasionally flaky.
//
// This merge function is a stopgap, not a real fix: it can't tell
// which write "should" win (Apollo doesn't pass merge functions any
// info about which operation triggered them), so it falls back to a
// heuristic — prefer a non-empty array over an empty one, since the
// nested nobody-reads-it selection is the one going stale/incomplete,
// not the direct Lease_ById query. Known gap: removing the very LAST
// well from a lease also produces a legitimately empty incoming array,
// which this heuristic will incorrectly suppress in favor of the
// stale non-empty one until a fresh network fetch (e.g. a page
// reload) writes to an empty cache with no `existing` to compare
// against. The real fix is to stop selecting `lease.wells` (and the
// equivalent nested `wells` under Tank/Allocation queries — same
// pattern, same root cause) in operations that never read it, so
// there's only ever one writer of Lease.wells and this policy can be
// deleted entirely.
const cache = new InMemoryCache({
   typePolicies: {
      Lease: {
         fields: {
            wells: {
               merge(existing: unknown[] | undefined, incoming: unknown[] | undefined) {
                  if (!existing || existing.length === 0) return incoming;
                  if (!incoming || incoming.length === 0) return existing;
                  return incoming;
               },
            },
         },
      },
      // PricingConfig does have an `id` (UUID) field, but per Duane's call
      // it isn't the entity's real identity — `effectiveDate` is (the
      // backend enforces at most one config per date; see
      // custom/PricingConfig_ByEffectiveDate.graphql's header comment and
      // custom/README.md's dated section for the full reasoning). Apollo's
      // default normalization keys every type by `id` whenever one is
      // present, which would treat "the config effective 2026-08-17" as a
      // *different* cache entity before and after an edit if the backend
      // ever returns it under a different id across requests — overriding
      // keyFields to `effectiveDate` instead makes Apollo normalize (and
      // merge writes into) the same cache entry for "the config for this
      // date" regardless of what id comes back with it, so
      // pricingConfig_ByEffectiveDate's result and its matching row in
      // pricingConfig_All always resolve to one shared cache entity, and a
      // pricingConfig_CreateUpdate response updates that same entity in
      // place. Every PricingConfig-returning operation in custom/ selects
      // `effectiveDate` for exactly this reason — Apollo can't extract a
      // keyFields value from a field that wasn't selected.
      PricingConfig: {
         keyFields: ['effectiveDate'],
      },
      // TanksAllocationsReportPage.tsx's report_TankAllocations query
      // selects `id` on each row (Report_TankAllocations.graphql), which
      // is very likely the underlying Tank's own id — stable regardless
      // of which `asOfDate` was queried, since a tank has one identity
      // but a different allocation snapshot per date. Apollo's default
      // normalization keys ANY type with an `id` field by that id alone,
      // so without this override, viewing date A (writes TankAllocation:
      // <tankId> with A's fromDate/toDate/allocation), then date B
      // (overwrites those same normalized entities with B's values),
      // then going back to A re-reads the cached ROOT_QUERY entry for
      // "asOfDate: A" — which still points at the SAME entities, now
      // holding B's values — so the table would flash B's numbers under
      // an "as of A" label until the accompanying network refetch (this
      // query defaults to cache-and-network) corrects it a moment later.
      // Exact same bug class already found and fixed for PricingConfig
      // above (same root cause: a type's `id` field isn't its real
      // identity for this app's purposes) — `keyFields: false` is the
      // simpler fix here since, unlike PricingConfig, nothing else in the
      // app ever looks up a TankAllocation by id or needs it normalized
      // for cross-query merging; these are read-only, point-in-time
      // computed report rows, never mutated in place, so there is no
      // benefit to normalizing them at all. This turns them into plain
      // embedded objects instead — each `report_TankAllocations` result
      // is now stored, read, and overwritten as one self-contained list
      // under its own `asOfDate`/`wellIds`/`pumperIds`-keyed field entry,
      // with no shared entity to collide across dates.
      TankAllocation: {
         keyFields: false,
      },
   },
});

// ADDED 2026-09-11 (Duane): "Add RetryLink so a query or mutation
// retries a few times before failing" for infrequent network blips.
// Scoped to QUERIES ONLY, never mutations: mutations already have
// their own retry-equivalent strategy (useOfflineMutation.ts's single
// online attempt + queue-and-replay-with-backoff via useOfflineSync.ts,
// 2026-08-27/2026-09-04) that a transparent link-level retry would only
// fight with. It would retry a mutation before useOfflineMutation's own
// catch block ever sees the failure, delaying the queue fallback past
// the per-operation timeout ceilings tuned above, and risks a genuine
// duplicate write for any mutation where a client-side failure doesn't
// guarantee the server-side write never landed — exactly the class of
// bug already hit once in runTicketPhotoSync.ts's photo-attach race.
//
// Queries have no such safety net today: a transient blip either shows
// stale cache data quietly (fine, already covered by cache-and-network
// below) or surfaces a hard error with nothing retrying it until the
// next 30s poll tick or a manual reload. This closes that gap: up to 2
// retries, short exponential backoff with jitter, and only for a
// genuine transport-level failure — never a GraphQL error, never a 401
// (authFailureLink's own streak logic below already owns that
// decision and must see it undisturbed), and never our own
// timeoutLink abort (retrying past a ceiling this app deliberately
// chose would just double the wait, not fix anything).
//
// Placement: this sits OUTSIDE (before) authFailureLink in the
// `from([...])` chain below, so authFailureLink only observes the
// final, fully-retried outcome of a query — not every individual
// retry attempt. That keeps its 401-streak counter and
// markOnlineFromRequestOutcome reachability signal counting one
// logical operation once, same as before this link existed, rather
// than once per retry.
const retryLink = new RetryLink({
   delay: {
      initial: 300,
      max: 2000,
      jitter: true,
   },
   attempts: {
      max: 2,
      retryIf: (error, operation) => {
         if (!error) return false;
         const definition = getMainDefinition(operation.query);
         const isQuery = definition.kind === 'OperationDefinition' && definition.operation === 'query';
         if (!isQuery) return false;
         // error !== null is already guaranteed here: the `if (!error) return false;`
         // guard above already excludes null (it's falsy), same as any other falsy value.
         const statusCode =
            typeof error === 'object' && 'statusCode' in error
               ? (error as {statusCode?: unknown}).statusCode
               : undefined;
         if (statusCode === 401) return false;
         return !(error instanceof Error && /abort|timeout/i.test(error.message));
      },
   },
});

export const apolloClient = new ApolloClient({
   link: from([retryLink, authFailureLink, authLink, timeoutLink, httpLink]),
   cache,
   defaultOptions: {
      watchQuery: {
         // Favor cached well/barrel-test data when offline rather than erroring out.
         fetchPolicy: 'cache-and-network',
         errorPolicy: 'all',
      },
      query: {
         fetchPolicy: 'cache-first',
         errorPolicy: 'all',
      },
      // Added 2026-08-21 — every mutation call site in the app now passes
      // errorPolicy: 'all' explicitly too (see state/useOfflineMutation.ts,
      // state/useOfflineSync.ts, state/runTicketPhotoSync.ts,
      // lib/allocationSync.ts), each with its own data-presence check
      // rather than relying on whether mutate() throws. This default is a
      // backstop for whichever of those a future call site forgets:
      // without it, Apollo's own mutation default (errorPolicy: 'none')
      // discards a successful response's `data` and throws whenever any
      // `errors` entry comes back alongside it — even something cosmetic,
      // unrelated to whether the write itself succeeded. That's the exact,
      // confirmed root cause of the run ticket photo-attach bug fixed the
      // same day (Duane's captured network trace showed the mutation
      // succeeding every time while the client treated it as a failure and
      // deleted the just-uploaded photo) — see
      // state/runTicketPhotoSync.ts's doc comments for the full story.
      mutate: {
         errorPolicy: 'all',
      },
   },
});

// IndexedDB-backed storage for the persisted cache, via localforage
// (configured to force its IndexedDB driver rather than falling back
// to WebSQL/localStorage). This is separate from the offline
// mutation queue's own IndexedDB store in lib/offlineDb.ts — this one
// persists *query results*, that one persists *pending writes*.
const queryCacheStorage = localforage.createInstance({
   name: 'well-data-app',
   storeName: 'apollo-query-cache',
   driver: localforage.INDEXEDDB,
});

let persistencePromise: Promise<void> | null = null;

// Some browser storage contexts (private browsing, blocked/partitioned
// IndexedDB, certain embedded/preview environments, ...) never call
// back an IndexedDB request's onsuccess *or* onerror — the request
// just hangs forever. persistCache()/localforage ultimately sit on
// top of IndexedDB, so without a timeout that hang would propagate
// all the way up to bootstrapAuthAtom and leave the app stuck on its
// loading spinner indefinitely. 5s is generous for a local IndexedDB
// read/init; anything slower than that is almost certainly stalled,
// not just "taking a while".
const CACHE_PERSISTENCE_TIMEOUT_MS = 5000;

/**
 * Restores the Apollo cache from IndexedDB (if anything was
 * persisted from a prior session) and starts persisting it on every
 * subsequent write.
 *
 * Call this ONLY for roles that need offline reads (regular/field
 * users — see types/auth.ts isFieldRole). Admin/SuperAdmin sessions
 * should never call this: they stay in-memory-only, which is correct
 * for an always-online office role and avoids writing anything to
 * IndexedDB for them.
 *
 * Safe to call multiple times — restoration + listener setup only
 * happens once per app session. The returned promise always settles
 * within CACHE_PERSISTENCE_TIMEOUT_MS (see withTimeout.ts): callers
 * that gate app readiness on this (e.g. bootstrapAuthAtom) can safely
 * await it without risking an indefinite hang. If persistCache itself
 * later resolves or rejects after the timeout already returned, that
 * result is just logged/ignored — the cache may still end up
 * persisted in the background even though this call didn't wait for it.
 */
export function enableOfflineCachePersistence(): Promise<void> {
   if (!persistencePromise) {
      persistencePromise = persistCache({
         cache,
         storage: queryCacheStorage as unknown as Parameters<typeof persistCache>[0]['storage'],
      }).catch((err) => {
         // Persistence failing to set up should never crash the app or
         // block the caller — log and move on; the session just runs
         // without offline cache persistence.
         console.error('Offline cache persistence failed to initialize:', err);
      });
   }

   return withTimeout(persistencePromise, CACHE_PERSISTENCE_TIMEOUT_MS, () => {
      console.warn(
         `Offline cache persistence hasn't finished after ${CACHE_PERSISTENCE_TIMEOUT_MS}ms ` +
            '(IndexedDB may be blocked or stalled) — continuing without waiting for it. ' +
            'It may still complete in the background.',
      );
   });
}
